import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { EmployeeDocument } from '../types/documents';
import { CATEGORY_LABELS } from '../api/documents';
import { ApiError } from '../api/client';
import { useToast } from './Toast';

const DOCUMENT_TYPES_BY_CATEGORY: Record<string, string[]> = {
  identity: ['Passport Photograph', 'National ID/NIN', 'International Passport', "Driver's Licence"],
  education: ['Highest Educational Certificate', 'Other Certificate', 'Professional Certification', 'Training Certificate'],
  employment: ['Application/CV', 'Appointment Letter', 'Signed Acceptance Letter', 'Job Description', 'Contract/Agreement', 'Confirmation Letter', 'Promotion Letter', 'Transfer Letter', 'Disciplinary Letter', 'Exit Document'],
  payroll: ['Bank Account Evidence', 'Tax Information', 'Pension/PFA Information', 'Other Statutory Document'],
  other: ['Reference Letter', 'Background Check', 'Policy Acknowledgement', 'Confidentiality/NDA', 'Other'],
};

interface Props {
  documents: EmployeeDocument[];
  downloadUrl: (documentId: number) => string;
  upload: (input: { fileDataUri: string; originalFilename: string; documentType: string; category: string }) => Promise<unknown>;
  onDelete: (documentId: number) => Promise<unknown>;
  queryKeyToInvalidate: unknown[];
  /**
   * The identity/education/employment/payroll/other picker only means
   * something for an employee's personal file. Every other module
   * (Programme, Finance, Projects...) passes false: category is always
   * sent as 'other' (still a valid value for the backend's enum, so no
   * schema change needed), Document Type becomes free text instead of
   * the fixed employee-shaped list, and documents render as one flat
   * list instead of grouped under category headings that would mostly
   * be empty. Defaults true so the two existing self-service/HR call
   * sites are unaffected.
   */
  hrCategories?: boolean;
}

/** Shared between My Profile (self-service), HR's Staff Detail page, and now every other module's "supporting documents" tab - same real upload/list/download behaviour either way, just different API endpoints (and, per hrCategories, different category UX) passed in via props. */
export function DocumentsSection({ documents, downloadUrl, upload, onDelete, queryKeyToInvalidate, hrCategories = true }: Props) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState('identity');
  const { register, handleSubmit, reset } = useForm<{ documentType: string }>();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: (input: { fileDataUri: string; originalFilename: string; documentType: string; category: string }) => upload(input),
    onSuccess: () => { showSuccess('Document uploaded.'); queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate }); reset(); setFile(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to upload document.'),
    onSettled: () => setUploading(false),
  });

  const handleUpload = async (values: { documentType: string }) => {
    if (!file) { showError('Please select a file.'); return; }
    setUploading(true);
    try {
      const dataUri = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read the selected file.'));
        reader.readAsDataURL(file);
      });
      uploadMutation.mutate({ fileDataUri: dataUri, originalFilename: file.name, documentType: values.documentType, category: hrCategories ? category : 'other' });
    } catch (err) {
      setUploading(false);
      showError(err instanceof Error ? err.message : 'Failed to process the selected file.');
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (documentId: number) => onDelete(documentId),
    onSuccess: () => { showSuccess('Document deleted.'); queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete document.'),
  });

  const handleDelete = (documentId: number, filename: string) => {
    if (window.confirm(`Delete "${filename}"? This cannot be undone.`)) deleteMutation.mutate(documentId);
  };

  const grouped = documents.reduce<Record<string, EmployeeDocument[]>>((acc, doc) => {
    (acc[doc.category] ??= []).push(doc);
    return acc;
  }, {});

  return (
    <section className="card">
      <h2>Documents</h2>
      <form className="inline-form" onSubmit={handleSubmit(handleUpload)}>
        {hrCategories && (
          <label>
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        )}
        <label>
          Document Type
          {hrCategories ? (
            <select {...register('documentType', { required: true })}>
              {(DOCUMENT_TYPES_BY_CATEGORY[category] ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : (
            <input type="text" placeholder="e.g. Donor Agreement, Supporting Invoice, MOU" {...register('documentType', { required: true })} />
          )}
        </label>
        <label>File (PDF, JPEG, PNG, Word, Excel - max 5MB)<input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
        <button type="submit" disabled={uploading || uploadMutation.isPending}>{uploading || uploadMutation.isPending ? 'Uploading…' : 'Upload'}</button>
      </form>

      {hrCategories ? (
        Object.keys(CATEGORY_LABELS).map((cat) => (
          <div key={cat} className="document-category-group">
            <h3>{CATEGORY_LABELS[cat]}</h3>
            {(grouped[cat] ?? []).length === 0 ? (
              <p className="muted">No documents uploaded yet.</p>
            ) : (
              <ul className="deadline-list">
                {grouped[cat].map((doc) => (
                  <li key={doc.id}>
                    {doc.document_type} - {doc.original_filename} ({Math.round(doc.size_bytes / 1024)}KB){' '}
                    <a href={downloadUrl(doc.id)} target="_blank" rel="noreferrer">Download</a>{' '}
                    <button className="link-button" onClick={() => handleDelete(doc.id, doc.original_filename)} disabled={deleteMutation.isPending}>Delete</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      ) : (
        documents.length === 0 ? (
          <p className="muted">No documents uploaded yet.</p>
        ) : (
          <ul className="deadline-list">
            {documents.map((doc) => (
              <li key={doc.id}>
                {doc.document_type} - {doc.original_filename} ({Math.round(doc.size_bytes / 1024)}KB){' '}
                <a href={downloadUrl(doc.id)} target="_blank" rel="noreferrer">Download</a>{' '}
                <button className="link-button" onClick={() => handleDelete(doc.id, doc.original_filename)} disabled={deleteMutation.isPending}>Delete</button>
              </li>
            ))}
          </ul>
        )
      )}
    </section>
  );
}
